/**
 * Freight Exceptions · Sprint 9.1 UI.
 *
 * Sub-workspace under Receiving. Presentation-only per LAW-007 — every
 * action reuses an existing backend endpoint:
 *
 *   POST /api/receiving/freight/parse                 (upload freight PDF)
 *   GET  /api/receiving/freight/exceptions            (queue)
 *   GET  /api/receiving/freight/auto-applied          (read-only history)
 *   POST /api/receiving/freight/exceptions/{id}/accept?receipt_id=…
 *   POST /api/receiving/freight/exceptions/{id}/reject?reason=…
 *
 * No matching / validation / FIFO logic lives here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadSimple, CheckCircle, XCircle, Warning, Info, CaretRight,
  ArrowLeft, ClockClockwise, ShieldCheck,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtCalendarDateShort } from "@/lib/calendarDate";
import EmptyState from "@/components/common/EmptyState";

const CONFIDENCE_THRESHOLD = 0.9;

export default function FreightExceptions() {
  const [tab, setTab] = useState("open");          // 'open' | 'applied'
  const [exceptions, setExceptions] = useState([]);
  const [applied, setApplied]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [uploading, setUploading]   = useState(false);
  const fileInput = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([
        api.get("/receiving/freight/exceptions", { params: { status: "open" } }),
        api.get("/receiving/freight/auto-applied"),
      ]);
      // Sort exceptions: lowest confidence first, then oldest first
      const rows = (q.data.exceptions || []).slice().sort((a, b) => {
        const c = (a.confidence ?? 0) - (b.confidence ?? 0);
        if (c !== 0) return c;
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
      setExceptions(rows);
      setApplied(a.data.auto_applied || []);
    } catch {
      toast.error("Could not load freight exceptions.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        "/receiving/freight/parse?carrier=AARAY",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const applied = data.auto_applied?.length || 0;
      const excs = data.exceptions_created?.length || 0;
      if (applied && !excs) {
        toast.success(`Freight invoice reconciled · ${applied} line(s) auto-applied.`);
      } else if (applied && excs) {
        toast.info(`${applied} auto-applied · ${excs} sent to exception queue.`);
      } else if (excs) {
        toast.warning(`${excs} line(s) require human review — see queue below.`);
      } else {
        toast.info("Freight invoice parsed with no matching receipts.");
      }
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Freight parse failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="freight-exceptions-page">
      <ReceivingSubNav active="exceptions" />
      <header>
        <div className="text-xs text-slate-500 uppercase tracking-wide">v1.2.2 · Sprint 9.1</div>
        <h1 className="font-display text-2xl font-semibold text-slate-900">Freight Exceptions</h1>
        <p className="text-sm text-slate-600 max-w-3xl mt-1">
          Freight invoices that the auto-parser cannot confidently reconcile land here.
          Everything else is applied automatically — you should only see what needs a decision.
        </p>
      </header>

      <UploadStrip uploading={uploading} onFile={doUpload} inputRef={fileInput} />

      <TabStrip
        tab={tab}
        onChange={setTab}
        openCount={exceptions.length}
        appliedCount={applied.length}
      />

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="fx-loading">Loading…</div>
      ) : tab === "open" ? (
        <ExceptionQueue rows={exceptions} onSelect={setSelected} />
      ) : (
        <AutoAppliedHistory rows={applied} />
      )}

      {selected && (
        <ExceptionDetailDrawer
          exception={selected}
          onClose={() => setSelected(null)}
          onResolved={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------
// Sub-navigation strip · sits under the top-level Receiving item.
// --------------------------------------------------------------------
export function ReceivingSubNav({ active }) {
  const tabs = [
    { key: "receipts",    label: "Receipts",              to: "/receiving" },
    { key: "reconciliation", label: "Receipt Reconciliation", to: "/receiving" },
    { key: "exceptions",  label: "Freight Exceptions",    to: "/receiving/freight-exceptions" },
  ];
  return (
    <nav className="flex items-center gap-1 border-b border-slate-200" data-testid="receiving-subnav">
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.to}
          data-testid={`receiving-subnav-${t.key}`}
          className={[
            "px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px",
            active === t.key
              ? "border-emerald-600 text-emerald-700"
              : "border-transparent text-slate-500 hover:text-slate-800",
          ].join(" ")}
        >
          {t.label}
        </Link>
      ))}
    </nav>
  );
}

// --------------------------------------------------------------------
// Upload strip
// --------------------------------------------------------------------
function UploadStrip({ uploading, onFile, inputRef }) {
  return (
    <div
      className="flex items-center justify-between gap-4 rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-3"
      data-testid="fx-upload-strip"
    >
      <div className="flex items-center gap-3">
        <UploadSimple size={22} className="text-slate-500" />
        <div>
          <div className="text-sm font-semibold text-slate-900">Upload AARAY Freight Invoice</div>
          <div className="text-xs text-slate-500">
            Confident matches (≥90%) apply automatically. Anything else lands here for review.
          </div>
        </div>
      </div>
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          data-testid="fx-upload-input"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid="fx-upload-btn"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {uploading ? "Parsing…" : "Upload freight PDF"}
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------
// Tab strip
// --------------------------------------------------------------------
function TabStrip({ tab, onChange, openCount, appliedCount }) {
  return (
    <div className="flex items-center gap-2 border-b border-slate-200" data-testid="fx-tabs">
      <TabBtn active={tab === "open"} onClick={() => onChange("open")}
              testid="fx-tab-open">
        Exception Queue <Count>{openCount}</Count>
      </TabBtn>
      <TabBtn active={tab === "applied"} onClick={() => onChange("applied")}
              testid="fx-tab-applied">
        Auto-Applied History <Count muted>{appliedCount}</Count>
      </TabBtn>
    </div>
  );
}
function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={[
        "-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors",
        active
          ? "border-emerald-600 text-emerald-700"
          : "border-transparent text-slate-500 hover:text-slate-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
function Count({ children, muted }) {
  return (
    <span
      className={[
        "ml-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums",
        muted ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-800",
      ].join(" ")}
    >
      {children}
    </span>
  );
}

// --------------------------------------------------------------------
// Exception Queue
// --------------------------------------------------------------------
function ExceptionQueue({ rows, onSelect }) {
  if (!rows.length) {
    return (
      <EmptyState
        icon={ShieldCheck}
        title="Every freight invoice has reconciled cleanly."
        why="Freight lines only land here when the parser can't confidently match them to a pending receipt."
        next="Upload an AARAY freight PDF using the button above — confident matches (≥90%) auto-apply, everything else lands here for review."
        testid="fx-empty"
      />
    );
  }
  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white" data-testid="fx-queue">
      <table className="w-full text-sm">
        <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
          <tr>
            <Th>Invoice #</Th>
            <Th>Carrier</Th>
            <Th>Delivery Date</Th>
            <Th>Supplier</Th>
            <Th>Terminal</Th>
            <Th>Candidate Receipt</Th>
            <Th className="text-center">Confidence</Th>
            <Th>Status</Th>
            <Th />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => <Row key={r.id} r={r} onClick={() => onSelect(r)} />)}
        </tbody>
      </table>
    </div>
  );
}
function Th({ children, className = "" }) {
  return <th className={`px-3 py-2 text-left font-semibold ${className}`}>{children}</th>;
}
function Row({ r, onClick }) {
  const line = r.freight_line || {};
  const report = r.confidence_report || {};
  const matched = report.matched_receipt;
  const confidencePct = Math.round((r.confidence || 0) * 100);
  return (
    <tr
      onClick={onClick}
      data-testid={`fx-row-${r.id}`}
      className="cursor-pointer hover:bg-slate-50"
    >
      <Td className="font-mono text-xs">{r.invoice_number || "—"}</Td>
      <Td>{r.carrier || "—"}</Td>
      <Td>{fmtCalendarDateShort(line.delivery_date) || "—"}</Td>
      <Td className="truncate max-w-[180px]">{line.supplier || "—"}</Td>
      <Td className="truncate max-w-[180px]">{line.terminal || "—"}</Td>
      <Td className="text-xs">
        {matched ? (
          <span>
            {fmtCalendarDateShort(matched.delivery_date)} · {matched.product || "—"} · {matched.litres} L
          </span>
        ) : (
          <span className="text-slate-400">No candidate</span>
        )}
      </Td>
      <Td className="text-center">
        <ConfidencePill pct={confidencePct} />
      </Td>
      <Td>
        <StatusPill status={r.status} />
      </Td>
      <Td className="text-right pr-4">
        <CaretRight size={14} className="text-slate-400 inline" />
      </Td>
    </tr>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 align-middle ${className}`}>{children}</td>;
}
function ConfidencePill({ pct }) {
  const cls =
    pct >= 90 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : pct >= 60 ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-rose-50 text-rose-700 border-rose-200";
  return (
    <span className={`inline-flex min-w-[52px] justify-center rounded-full border px-2 py-0.5 text-xs font-semibold tabular-nums ${cls}`}
          data-testid={`fx-confidence-${pct}`}>
      {pct}%
    </span>
  );
}
function StatusPill({ status }) {
  const label = status === "open" ? "Needs review"
             : status === "accepted" ? "Accepted"
             : status === "rejected" ? "Rejected"
             : status || "—";
  const cls = status === "open"
    ? "bg-amber-100 text-amber-800 border-amber-200"
    : status === "accepted"
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

// --------------------------------------------------------------------
// Detail drawer
// --------------------------------------------------------------------
function ExceptionDetailDrawer({ exception, onClose, onResolved }) {
  const report = exception.confidence_report || {};
  const line   = exception.freight_line || {};
  const candidates = report.candidates || [];
  const matched = report.matched_receipt;
  const [selectedReceiptId, setSelectedReceiptId] = useState(matched?.id || null);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const pct = Math.round((exception.confidence || 0) * 100);

  const doAccept = async (receiptId) => {
    if (!receiptId) {
      toast.error("Pick a candidate receipt first.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/receiving/freight/exceptions/${exception.id}/accept`,
        null,
        { params: { receipt_id: receiptId } }
      );
      toast.success("Freight applied to receipt.");
      onResolved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Accept failed.");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!reason.trim()) {
      toast.error("A reason is required to reject.");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/receiving/freight/exceptions/${exception.id}/reject`,
        null,
        { params: { reason: reason.trim() } }
      );
      toast.success("Exception rejected.");
      onResolved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Reject failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="fx-drawer">
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <aside className="w-full max-w-2xl overflow-y-auto bg-white shadow-xl">
        <header className="border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">Freight Exception</div>
            <h2 className="font-display text-xl font-semibold text-slate-900">
              {exception.carrier} · Invoice #{exception.invoice_number}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="fx-drawer-close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          >
            <XCircle size={20} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {/* Overall confidence */}
          <section data-testid="fx-drawer-confidence">
            <div className="flex items-baseline gap-3">
              <div className="text-xs text-slate-500 uppercase tracking-wide">Overall Confidence</div>
              <ConfidencePill pct={pct} />
            </div>
          </section>

          {/* Candidate match summary */}
          <section data-testid="fx-drawer-candidate">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Candidate Match</div>
            {matched ? (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <Field label="Delivery Date" value={fmtCalendarDateShort(matched.delivery_date)} />
                  <Field label="Product"       value={matched.product || "—"} />
                  <Field label="Litres"        value={matched.litres || "—"} />
                  <Field label="Terminal"      value={matched.terminal || "—"} />
                  <Field label="Supplier"      value={matched.supplier || "—"} className="col-span-2" />
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-500">
                No candidate receipt matched this freight line.
              </div>
            )}
          </section>

          {/* Freight line values */}
          <section data-testid="fx-drawer-freight-line">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Freight Line</div>
            <div className="rounded-md border border-slate-200 bg-white p-3 text-sm">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                <Field label="Delivery Date" value={fmtCalendarDateShort(line.delivery_date)} />
                <Field label="Terminal"      value={line.terminal || "—"} />
                <Field label="Litres"        value={line.litres || "—"} />
                <Field label="Reference"     value={line.reference || "—"} />
                <Field label="Freight"       value={line.freight_amount ? `$${line.freight_amount}` : "—"} />
                <Field label="Surcharge"     value={line.fuel_surcharge ? `$${line.fuel_surcharge}` : "—"} />
              </div>
            </div>
          </section>

          {/* Validation checks */}
          <section data-testid="fx-drawer-checks">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Validation Checks</div>
            <ul className="grid grid-cols-2 gap-1 text-sm">
              {(report.checks || []).map((c, i) => (
                <li key={i} className="flex items-center gap-2">
                  {c.passed
                    ? <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                    : <Warning size={16} weight="fill" className="text-amber-500" />}
                  <span className={c.passed ? "text-slate-700" : "text-slate-900 font-medium"}>
                    {c.label}
                  </span>
                  {c.detail && <span className="text-xs text-slate-500">· {c.detail}</span>}
                </li>
              ))}
            </ul>
          </section>

          {/* Why manual review */}
          <section data-testid="fx-drawer-reason">
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">Why Manual Review?</div>
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
              <Info size={16} className="mt-0.5" />
              <div>{exception.reason || "Confidence below auto-apply threshold."}</div>
            </div>
            {(report.warnings || []).length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-slate-600 pl-6 list-disc">
                {report.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </section>

          {/* Candidate list (for Choose Different Receipt) */}
          {candidates.length > 0 && (
            <section data-testid="fx-drawer-candidates">
              <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">
                All Candidate Receipts
              </div>
              <ul className="divide-y divide-slate-200 rounded-md border border-slate-200">
                {candidates.map((c) => {
                  const isPicked = c.receipt_id === selectedReceiptId;
                  const cpct = Math.round((c.score || 0) * 100);
                  return (
                    <li key={c.receipt_id}>
                      <label className={[
                        "flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer",
                        isPicked ? "bg-emerald-50" : "hover:bg-slate-50",
                      ].join(" ")}>
                        <div className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="candidate"
                            checked={isPicked}
                            onChange={() => setSelectedReceiptId(c.receipt_id)}
                            data-testid={`fx-candidate-radio-${c.receipt_id}`}
                          />
                          <div>
                            <div className="font-medium text-slate-900">
                              {fmtCalendarDateShort(c.receipt?.delivery_date)} · {c.receipt?.product || "—"} · {c.receipt?.litres} L
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {c.receipt?.terminal || "—"} · {c.receipt?.supplier || "—"}
                              {c.litre_delta_pct != null && (
                                <> · Δ {(c.litre_delta_pct * 100).toFixed(2)}%</>
                              )}
                            </div>
                          </div>
                        </div>
                        <ConfidencePill pct={cpct} />
                      </label>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Actions */}
          <section className="border-t border-slate-200 pt-4" data-testid="fx-drawer-actions">
            {rejectMode ? (
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Reason (required)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="fx-reject-reason"
                  placeholder="Explain why this freight invoice cannot be applied…"
                  rows={3}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setRejectMode(false)}
                    className="rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
                    data-testid="fx-reject-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={doReject}
                    disabled={busy || !reason.trim()}
                    data-testid="fx-reject-confirm"
                    className="rounded-md bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
                  >
                    Reject exception
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => doAccept(selectedReceiptId)}
                  disabled={busy || !selectedReceiptId}
                  data-testid="fx-accept-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  <CheckCircle size={16} weight="fill" />
                  {selectedReceiptId === matched?.id ? "Accept recommended match" : "Accept selected receipt"}
                </button>
                <button
                  type="button"
                  onClick={() => setRejectMode(true)}
                  disabled={busy}
                  data-testid="fx-reject-btn"
                  className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50"
                >
                  <XCircle size={16} />
                  Reject
                </button>
              </div>
            )}
          </section>
        </div>
      </aside>
    </div>
  );
}
function Field({ label, value, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900">{value}</div>
    </div>
  );
}

// --------------------------------------------------------------------
// Auto-Applied history
// --------------------------------------------------------------------
function AutoAppliedHistory({ rows }) {
  if (!rows.length) {
    return (
      <EmptyState
        icon={ClockClockwise}
        title="Nothing has been auto-applied yet"
        why="This is the read-only history of every freight invoice the parser reconciled without needing human review."
        next="Once you upload a freight PDF that scores ≥90% confidence against a pending receipt, it'll appear here."
        testid="fx-applied-empty"
      />
    );
  }
  return (
    <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white" data-testid="fx-applied-list">
      {rows.map((r) => {
        const conf = extractConfidence(r.notes);
        return (
          <li key={`${r.receipt_id}-${r.attached_at}`} className="px-4 py-3 flex items-center justify-between gap-4"
              data-testid={`fx-applied-row-${r.receipt_id}`}>
            <div className="flex items-center gap-3">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              <div>
                <div className="text-sm font-semibold text-slate-900">
                  {r.carrier || "—"} · Invoice #{r.freight_invoice_number || "—"}
                </div>
                <div className="text-[11px] text-slate-500">
                  Applied {r.attached_at ? new Date(r.attached_at).toLocaleString() : "—"} · by {r.attached_by || "system"}
                </div>
                <div className="text-[11px] text-slate-500">
                  Delivery {fmtCalendarDateShort(r.delivery_date)} · {r.terminal || "—"} · Supplier receipt {(r.receipt_id || "").slice(0,8)}…
                </div>
              </div>
            </div>
            <div className="text-right">
              {conf != null && (
                <div className="text-xs text-slate-500">Confidence <ConfidencePill pct={conf} /></div>
              )}
              <div className="mt-1 text-sm font-mono tabular-nums text-slate-800">
                ${r.freight_per_litre}/L
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
function extractConfidence(notes) {
  if (!notes) return null;
  const m = /confidence\s+(\d+)%/i.exec(notes);
  return m ? Number(m[1]) : null;
}
